import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../assets/logoMoovicar.png';
import env from '../../config/env';
import { useAxios } from '../../utils/hook/useAxios';

function SigninPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();

  const registered = searchParams.get('registered') === '1';
  const verified = searchParams.get('verified') === '1';
  const emailSent = searchParams.get('emailSent') !== '0';
  const verificationError = searchParams.get('verificationError') === '1';
  const axios = useAxios();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await axios.post('/auth/login', { email, password });

      // Redirige vers la page d'origine si disponible, sinon l'accueil
      const redirectTo = new URLSearchParams(window.location.search).get('redirect') || '/';
      window.location.href = redirectTo;
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Email ou mot de passe incorrect. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-vh-100 d-flex flex-column align-items-center justify-content-center bg-body-tertiary py-4">
      <ToastContainer position="top-center" />

      <div style={{ width: '100%', maxWidth: 420, padding: '0 1rem' }}>
        {/* Logo */}
        <div className="text-center mb-4">
          <Link to="/">
            <img src={logo} alt="Moovicar" height="48" />
          </Link>
        </div>

        {/* Card */}
        <div className="card border-0 shadow-sm rounded-4 p-4">
          <h4 className="fw-bold mb-1 text-center">Connexion</h4>
          <p className="text-muted text-center mb-4" style={{ fontSize: 14 }}>
            Bienvenue ! Entrez vos identifiants.
          </p>

          {registered && (
            <div
              className={`alert ${emailSent ? 'alert-success' : 'alert-warning'} rounded-3`}
              role="status"
            >
              <div className="fw-semibold mb-1">
                {emailSent ? 'Inscription réussie !' : 'Compte créé, email non envoyé'}
              </div>
              <div className="small">
                {emailSent
                  ? 'Un email de vérification vous a été envoyé. Consultez votre boîte de réception et cliquez sur le lien pour activer votre compte avant de vous connecter.'
                  : 'Votre compte existe, mais nous n’avons pas pu envoyer le lien de vérification. Contactez notre assistance ou demandez un nouvel email.'}
              </div>
            </div>
          )}

          {verified && (
            <div className="alert alert-success rounded-3" role="status">
              <div className="fw-semibold mb-1">Adresse email vérifiée !</div>
              <div className="small">
                Votre compte est activé. Vous pouvez maintenant vous connecter.
              </div>
            </div>
          )}

          {verificationError && (
            <div className="alert alert-danger rounded-3" role="alert">
              Le lien de vérification est invalide ou a expiré. Veuillez demander un nouveau lien.
            </div>
          )}

          {/* Google */}
          <a
            className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2 mb-3 rounded-3"
            href={`${env.baseUrlApi}/api/auth/google`}
            style={{ fontSize: 14, fontWeight: 500 }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
              <path fill="none" d="M0 0h48v48H0z" />
            </svg>
            Continuer avec Google
          </a>

          {/* Divider */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <hr className="flex-grow-1 m-0" />
            <span className="text-muted" style={{ fontSize: 12 }}>
              ou
            </span>
            <hr className="flex-grow-1 m-0" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label small fw-semibold text-muted">Email</label>
              <input
                type="email"
                className="form-control rounded-3"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="mb-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label small fw-semibold text-muted mb-0">Mot de passe</label>
                <Link to="/forgot-password" className="text-muted" style={{ fontSize: 12 }}>
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="position-relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  className="form-control rounded-3 pe-5"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="btn position-absolute top-50 end-0 translate-middle-y border-0 bg-transparent me-1"
                  onClick={() => setShowPwd((p) => !p)}
                  tabIndex={-1}
                  style={{ zIndex: 5 }}
                >
                  <i className={`bi ${showPwd ? 'bi-eye-slash' : 'bi-eye'} text-muted`}></i>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100 rounded-3 mt-4 py-2 fw-semibold"
              disabled={loading || !email || !password}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Connexion…
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>
        </div>

        {/* Lien inscription */}
        <p className="text-center text-muted mt-3" style={{ fontSize: 14 }}>
          Pas encore de compte ?{' '}
          <Link to="/signup" className="fw-semibold text-primary text-decoration-none">
            Créer un compte
          </Link>
        </p>
      </div>
    </section>
  );
}

export default SigninPage;
